import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, AlertTriangle, Loader2, Mail } from "lucide-react";
import { API } from "../lib/constants";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const uid = params.get("uid") || "";

  const [state, setState] = useState("verifying"); // "verifying" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const verify = async () => {
      if (!token || !uid) { setState("error"); setErrorMsg("Missing token."); return; }
      try {
        const r = await fetch(`${API}/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, uid }),
        });
        if (!r.ok) {
          let msg = "Verification failed.";
          try { const d = await r.json(); msg = d.detail || msg; } catch {}
          setErrorMsg(msg);
          setState("error");
          return;
        }
        setState("success");
      } catch {
        setErrorMsg("Network error. Please try again.");
        setState("error");
      }
    };
    verify();
  }, [token, uid]);

  return (
    <div className="min-h-screen pt-32 px-6 flex items-center justify-center" data-testid="verify-email-page">
      <div className="max-w-md text-center">
        {state === "verifying" && (
          <>
            <Loader2 size={40} className="animate-spin text-red-600 mx-auto mb-6" />
            <h1 className="font-cinzel text-3xl font-bold mb-3">Verifying…</h1>
            <p className="text-gray-400 text-sm">Confirming your email — one moment.</p>
          </>
        )}
        {state === "success" && (
          <div data-testid="verify-success">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h1 className="font-cinzel text-3xl font-bold mb-3">Email Confirmed</h1>
            <p className="text-gray-400 mb-8 text-sm leading-relaxed">
              Your account is now fully activated. Welcome to Red Ink Studios — the story awaits.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all"
              data-testid="verify-continue-btn"
            >
              Start Listening
            </button>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-error">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle size={40} className="text-amber-500" />
            </div>
            <h1 className="font-cinzel text-3xl font-bold mb-3">Verification Failed</h1>
            <p className="text-gray-400 mb-8 text-sm leading-relaxed">
              {errorMsg} You can request a new verification email from the banner on any page after signing in.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all flex items-center gap-2 mx-auto"
              data-testid="verify-home-btn"
            >
              <Mail size={16} /> Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
